import { Building2, CalendarClock, ChevronDown, ChevronUp, Globe, GraduationCap, Infinity as InfinityIcon, Layers, LayoutGrid, List, Pin, Plus, Settings } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import type { Collection } from '@/types/collection';
import type { ExperienceSummary, KanbanStatus } from '@/types/experience';
import type { Profile } from '@/types/profile';

import CollectionModal, { type CollectionDraft } from '@/components/collections/collection-modal';
import DashboardHeader from '@/components/dashboard/dashboard-header';
import DeleteExperienceModal from '@/components/dashboard/delete-experience-modal';
import ExperienceCard from '@/components/dashboard/experience-card';
import PublishModal from '@/components/dashboard/publish-modal';
import PublishedSuccessModal from '@/components/dashboard/published-success-modal';
import PublishedWarningModal from '@/components/dashboard/published-warning-modal';
import Toast from '@/components/toast';
import { Button } from '@/components/ui/button';
import EditableTitle from '@/components/ui/editable-title';
import SelectMenu from '@/components/ui/select-menu';
import { api } from '@/lib/api';
import { relativeTime } from '@/lib/relative-time';


// The board is the design→deployment pipeline: In Development → Complete → Deployed. Card position
// within a column carries no meaning here (unlike the builders), so there's no manual ordering —
// only a computed sort. Cards move stage via the per-card advance affordance and (soon) bulk actions.
type ColId = 'in-progress' | 'finished' | 'live';
type SortMode = 'recent' | 'oldest' | 'az' | 'za';
// List view sorts by a clicked column header (not the board's recency-style modes).
type ListKey = 'name' | 'category' | 'tasks' | 'points' | 'status' | 'updated';
const COLUMNS: { id: ColId; labelKey: string; headerBg: string }[] = [
  { id: 'in-progress', labelKey: 'board.columns.inDevelopment', headerBg: 'bg-ws-in-progress-bg' },
  { id: 'finished', labelKey: 'board.columns.complete', headerBg: 'bg-ws-neutral-bg' },
  { id: 'live', labelKey: 'board.columns.deployed', headerBg: 'bg-ws-success-bg' },
];

function deriveStatus(exp: ExperienceSummary): KanbanStatus {
  if (exp.kanbanStatus) return exp.kanbanStatus;
  return exp.status === 'published' ? 'live' : 'in-progress';
}
// Map any stored status onto one of the three pipeline columns (or the archived shelf).
function colOf(s: KanbanStatus): ColId | 'archived' {
  if (s === 'archived') return 'archived';
  if (s === 'on-hold') return 'in-progress';
  return s;
}

// Group a column's cards by their single `category` for the "group by category" view. Because
// category is one value per experience there are no duplicates; experiences without a category fall
// into an "Ungrouped" section (last). Headers use natural ordering so "Week 10" follows "Week 2".
function buildCategorySections(list: ExperienceSummary[], uncategorizedLabel: string): [string, ExperienceSummary[]][] {
  const groups = new Map<string, ExperienceSummary[]>();
  const uncategorized: ExperienceSummary[] = [];
  for (const e of list) {
    const cat = e.category?.trim();
    if (!cat) { uncategorized.push(e); continue; }
    const arr = groups.get(cat); if (arr) arr.push(e); else groups.set(cat, [e]);
  }
  const sorted = Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: 'base' }));
  if (uncategorized.length) sorted.push([uncategorizedLabel, uncategorized]);
  return sorted;
}

export default function CollectionBoardPage() {
  const { id: collectionId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [collection, setCollection] = useState<Collection | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [editingCollection, setEditingCollection] = useState(false);
  const [experiences, setExperiences] = useState<ExperienceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState<'board' | 'list'>('board');
  // Phone-only: which single pipeline column the segmented switcher is showing (sm+ shows all three).
  const [mobileCol, setMobileCol] = useState<ColId>('in-progress');
  const [search, setSearch] = useState('');
  const [listSort, setListSort] = useState<{ key: ListKey; dir: 'asc' | 'desc' }>({ key: 'updated', dir: 'desc' });
  // Per-column controls (In Development first). Its own sort overrides the toolbar sort for that
  // column; `inDevTag` filters it to one tag ('' = all). Tagging UX itself comes later.
  // Per-column sort (each column header has its own). The list view sorts by its column headers.
  const [colSort, setColSort] = useState<Record<ColId, SortMode>>({ 'in-progress': 'recent', 'finished': 'recent', 'live': 'recent' });
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [kanbanOverrides, setKanbanOverrides] = useState<Record<string, KanbanStatus>>({});
  const [pinnedOverrides, setPinnedOverrides] = useState<Record<string, boolean>>({});
  const [showArchived, setShowArchived] = useState(false);
  const [editWarningId, setEditWarningId] = useState<string | null>(null);
  const [publishModalId, setPublishModalId] = useState<string | null>(null);
  const [deleteModalId, setDeleteModalId] = useState<string | null>(null);
  const [publishedSuccess, setPublishedSuccess] = useState<{ name: string; shareToken: string } | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'info' | 'error' } | null>(null);
  const showToast = (msg: string, type: 'info' | 'error' = 'info') => setToast({ msg, type });

  useEffect(() => {
    if (!collectionId) return;
    setLoading(true);
    Promise.all([
      api.get<Collection>(`/collections/${collectionId}`),
      api.get<ExperienceSummary[]>(`/experiences?collectionId=${collectionId}`),
    ])
      .then(([col, exps]) => { setCollection(col); setExperiences(exps); })
      .catch(() => setError(t('board.errors.couldNotLoad')))
      .finally(() => setLoading(false));
    /*
     * `t` is deliberately not a dependency. It changes when the language does, and re-running
     * this would refetch the whole board to retranslate one error string that is usually not
     * even showing. The stale message is the cheaper wrong thing, and it is replaced the moment
     * anything reloads.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId]);

  useEffect(() => { api.get<Profile>('/profile').then(setProfile).catch(() => {}); }, []);

  const activeAffiliations = useMemo(() => (profile?.affiliations ?? []).filter((a) => a.active !== false), [profile]);
  // Affiliation objects this collection is linked to (by id), resolved from the profile.
  const collectionAffiliations = useMemo(() => {
    const ids = new Set(collection?.affiliationIds ?? []);
    return (profile?.affiliations ?? []).filter((a) => ids.has(a.id));
  }, [collection, profile]);
  function patchCollection(partial: Record<string, unknown>) {
    if (!collection) return;
    const prev = collection;
    setCollection({ ...collection, ...partial } as Collection);
    api.patch<Collection>(`/collections/${collection.id}`, { ...partial, version: collection.version })
      .then(setCollection)
      .catch(() => { setCollection(prev); showToast(t('board.errors.couldNotSave'), 'error'); });
  }
  function handleCollectionSave(draft: CollectionDraft) {
    patchCollection({ ...draft });
    setEditingCollection(false);
  }

  const getStatus = (e: ExperienceSummary) => kanbanOverrides[e.id] ?? deriveStatus(e);
  const isPinned = (e: ExperienceSummary) => pinnedOverrides[e.id] ?? (e.pinned ?? false);

  function moveToColumn(id: string, col: KanbanStatus) {
    const prev = kanbanOverrides[id];
    setKanbanOverrides((o) => ({ ...o, [id]: col }));
    const patch: Record<string, unknown> = { kanbanStatus: col };
    if (col !== 'live') patch.status = 'draft';
    api.patch(`/experiences/${id}`, patch).catch(() => {
      setKanbanOverrides((o) => { const n = { ...o }; if (prev === undefined) delete n[id]; else n[id] = prev; return n; });
      showToast(t('home.errors.failedMove'), 'error');
    });
  }
  function togglePin(exp: ExperienceSummary) {
    const next = !isPinned(exp);
    const prev = pinnedOverrides[exp.id];
    setPinnedOverrides((o) => ({ ...o, [exp.id]: next }));
    api.patch(`/experiences/${exp.id}`, { pinned: next }).catch(() => {
      setPinnedOverrides((o) => { const n = { ...o }; if (prev === undefined) delete n[exp.id]; else n[exp.id] = prev; return n; });
      showToast(t('home.errors.failedPin'), 'error');
    });
  }
  function handleDuplicate(id: string) {
    api.post<ExperienceSummary>(`/experiences/${id}/duplicate`)
      .then((copy) => setExperiences((p) => [...p, copy]))
      .catch(() => showToast(t('home.errors.failedDuplicate'), 'error'));
  }
  function handleConfirmDelete() {
    if (!deleteModalId) return;
    const id = deleteModalId;
    setDeleteModalId(null);
    api.delete(`/experiences/${id}`)
      .then(() => setExperiences((p) => p.filter((e) => e.id !== id)))
      .catch(() => showToast(t('home.errors.failedDelete'), 'error'));
  }
  function handleEdit(exp: ExperienceSummary) {
    if (getStatus(exp) === 'live') setEditWarningId(exp.id);
    else navigate(`/app/experience/${exp.id}`);
  }
  function handleConfirmPublish(config: { groups: string[]; deadline: Date | null; visibleAfterDeadline: boolean }) {
    if (!publishModalId) return;
    const id = publishModalId;
    setPublishModalId(null);
    api.patch<ExperienceSummary>(`/experiences/${id}`, {
      status: 'published', publishedGroups: config.groups,
      deadline: config.deadline?.toISOString() ?? null, visibleAfterDeadline: config.visibleAfterDeadline,
    }).then((updated) => {
      moveToColumn(id, 'live');
      setExperiences((p) => p.map((e) => (e.id === id ? { ...e, shareToken: updated.shareToken } : e)));
      if (updated.shareToken) setPublishedSuccess({ name: experiences.find((e) => e.id === id)?.title ?? '', shareToken: updated.shareToken });
    }).catch(() => showToast(t('home.errors.failedPublish'), 'error'));
  }
  function handleNewExperience() {
    if (!collectionId) return;
    api.post<ExperienceSummary>('/experiences', { title: t('home.untitledExperience'), collectionId })
      .then((exp) => navigate(`/app/experience/${exp.id}`))
      .catch(() => showToast(t('home.errors.failedCreate'), 'error'));
  }
  function handleCategorySave(id: string, category: string) {
    const prev = experiences.find((e) => e.id === id)?.category ?? null;
    setExperiences((p) => p.map((e) => (e.id === id ? { ...e, category: category || null } : e)));
    api.patch(`/experiences/${id}`, { category }).catch(() => {
      setExperiences((p) => p.map((e) => (e.id === id ? { ...e, category: prev } : e)));
      showToast(t('home.errors.failedTags'), 'error');
    });
  }
  function handleSummarySave(id: string, summary: string) {
    const prev = experiences.find((e) => e.id === id)?.summary ?? null;
    setExperiences((p) => p.map((e) => (e.id === id ? { ...e, summary } : e)));
    api.patch(`/experiences/${id}`, { summary }).catch(() => {
      setExperiences((p) => p.map((e) => (e.id === id ? { ...e, summary: prev } : e)));
      showToast(t('home.errors.failedSummary'), 'error');
    });
  }

  // Pinned always float first; then by the given sort mode. Recent/Oldest use last-edited time.
  const cmpFor = (mode: SortMode) => (a: ExperienceSummary, b: ExperienceSummary) => {
    const pa = isPinned(a) ? 0 : 1, pb = isPinned(b) ? 0 : 1;
    if (pa !== pb) return pa - pb;
    if (mode === 'az') return a.title.localeCompare(b.title);
    if (mode === 'za') return b.title.localeCompare(a.title);
    const at = new Date(a.updatedAt).getTime(), bt = new Date(b.updatedAt).getTime();
    return mode === 'oldest' ? at - bt : bt - at;
  };

  // Cards grouped per pipeline column. In Development uses its own sort + tag filter; the other
  // columns use the toolbar sort.
  const orderedByCol = useMemo(() => {
    const q = search.toLowerCase();
    const bucket: Record<ColId, ExperienceSummary[]> = { 'in-progress': [], 'finished': [], 'live': [] };
    for (const e of experiences) {
      if (q && !e.title.toLowerCase().includes(q)) continue;
      const c = colOf(getStatus(e));
      if (c === 'archived') continue;
      bucket[c].push(e);
    }
    (Object.keys(bucket) as ColId[]).forEach((k) => { bucket[k].sort(cmpFor(colSort[k])); });
    return bucket;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experiences, search, colSort, kanbanOverrides, pinnedOverrides]);

  const sortOptions = [
    { value: 'recent', label: t('board.sort.recent') },
    { value: 'oldest', label: t('board.sort.oldest') },
    { value: 'az', label: t('board.sort.az') },
    { value: 'za', label: t('board.sort.za') },
  ];

  const allCategories = useMemo(() => Array.from(new Set(experiences.map((e) => e.category).filter((c): c is string => !!c))).sort(), [experiences]);

  const statusRank = (e: ExperienceSummary) => {
    const c = colOf(getStatus(e));
    return c === 'in-progress' ? 0 : c === 'finished' ? 1 : c === 'live' ? 2 : 3;
  };
  function toggleSort(key: ListKey) {
    setListSort((s) => s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'name' || key === 'category' || key === 'status' ? 'asc' : 'desc' });
  }
  const filtered = useMemo(() => {
    const cmp = (a: ExperienceSummary, b: ExperienceSummary) => {
      let r = 0;
      if (listSort.key === 'name') r = a.title.localeCompare(b.title, undefined, { numeric: true, sensitivity: 'base' });
      else if (listSort.key === 'category') r = (a.category ?? '').localeCompare(b.category ?? '', undefined, { numeric: true, sensitivity: 'base' });
      else if (listSort.key === 'tasks') r = a.taskCount - b.taskCount;
      else if (listSort.key === 'points') r = (a.totalPoints ?? 0) - (b.totalPoints ?? 0);
      else if (listSort.key === 'status') r = statusRank(a) - statusRank(b);
      else r = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      return listSort.dir === 'asc' ? r : -r;
    };
    return experiences.filter((e) => !search || e.title.toLowerCase().includes(search.toLowerCase())).sort(cmp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experiences, search, listSort, kanbanOverrides]);
  const archived = filtered.filter((e) => colOf(getStatus(e)) === 'archived');

  return (
    <div className='flex h-screen flex-col overflow-hidden bg-ws-bg'>
      {/* NO BREADCRUMB. Both crumbs were duplicates: "Home" is where the logo already goes, and
          the collection's own title is the EditableTitle immediately below this bar. The level
          above a collection IS home, so the logo alone carries the trail — see the rule the task
          and experience bars follow (`TaskNavbar`): the logo and the page's parent, never the
          page itself and never Home. */}
      <DashboardHeader
        search={{ value: search, onChange: setSearch, placeholder: t('board.searchExperiences') }}
      />
      <div className='flex flex-1 flex-col overflow-hidden'>
        {/* Title */}
        <div className='flex flex-col gap-3 px-4 pt-5 pb-4 sm:px-6 [@media(max-height:520px)]:gap-1.5 [@media(max-height:520px)]:pt-2 [@media(max-height:520px)]:pb-2'>
          <div className='flex flex-col gap-3 md:flex-row md:items-start md:justify-between [@media(max-height:520px)]:gap-1.5'>
            <div className='min-w-0'>
              {/* Row 1: title, with the audience/availability swap chips inline on desktop but
                  dropped to their own line below the title on phone/tablet portrait (< lg). */}
              <div className='flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center'>
                {collection
                  ? <EditableTitle value={collection.title} onSave={(v) => patchCollection({ title: v })} placeholder={t('board.untitledCollection')} textClassName='text-2xl font-bold' />
                  : <h1 className='text-2xl font-bold text-ws-text'>…</h1>}
                {collection && (
                  <div className='flex flex-wrap items-center gap-2'>
                    {/* Audience — click swaps formal ⇄ informal */}
                    <button onClick={() => patchCollection({ governanceClass: collection.governanceClass === 'formal' ? 'informal' : 'formal' })} title={t('board.toggleAudience')}
                      className={`flex items-center gap-1 rounded border px-2 py-1 text-sm font-medium hover:bg-ws-hover ${collection.governanceClass ? 'border-ws-border text-ws-text' : 'border-dashed border-ws-border text-ws-muted'}`}>
                      {collection.governanceClass === 'formal' ? <GraduationCap className='h-4 w-4 text-ws-muted' /> : collection.governanceClass === 'informal' ? <Globe className='h-4 w-4 text-ws-muted' /> : null}
                      {collection.governanceClass ? t(`collections.governance.${collection.governanceClass}`) : t('collections.modal.audience')}
                    </button>
                    {/* Availability — click swaps limited-time ⇄ ongoing */}
                    <button onClick={() => patchCollection({ lifecycleClass: collection.lifecycleClass === 'time-boxed' ? 'perpetual' : 'time-boxed' })} title={t('board.toggleAvailability')}
                      className={`flex items-center gap-1 rounded border px-2 py-1 text-sm font-medium hover:bg-ws-hover ${collection.lifecycleClass ? 'border-ws-border text-ws-text' : 'border-dashed border-ws-border text-ws-muted'}`}>
                      {collection.lifecycleClass === 'time-boxed' ? <CalendarClock className='h-4 w-4 text-ws-muted' /> : collection.lifecycleClass === 'perpetual' ? <InfinityIcon className='h-4 w-4 text-ws-muted' /> : null}
                      {collection.lifecycleClass ? t(`collections.lifecycle.${collection.lifecycleClass === 'time-boxed' ? 'timeBoxed' : 'perpetual'}`) : t('collections.modal.lifecycle')}
                    </button>
                  </div>
                )}
              </div>
              {/* Row 2: category/type + linked affiliations + settings gear */}
              {collection && (
                <div className='mt-1.5 flex flex-wrap items-center gap-2 text-sm [@media(max-height:520px)]:mt-1'>
                  <button onClick={() => setEditingCollection(true)} title={t('board.editCollection')}
                    className={`flex items-center gap-1 rounded border px-2 py-1 font-medium hover:bg-ws-hover ${collection.label ? 'border-ws-border text-ws-text' : 'border-dashed border-ws-border text-ws-muted'}`}>
                    {collection.label ? collection.label : <><Plus className='h-3.5 w-3.5' />{t('board.addType')}</>}
                  </button>
                  {collectionAffiliations.length > 0 ? (
                    collectionAffiliations.map((a) => (
                      <button key={a.id} onClick={() => setEditingCollection(true)} title={a.location ? `${a.institution} · ${a.location}` : a.institution}
                        className='flex items-center gap-1 rounded border border-ws-border px-2 py-1 text-ws-text hover:bg-ws-hover'>
                        <Building2 className='h-4 w-4 text-ws-muted' />
                        <span className='max-w-[16rem] truncate'>{a.institution}</span>
                      </button>
                    ))
                  ) : (
                    <button onClick={() => setEditingCollection(true)} title={t('board.addAffiliation')}
                      className='flex items-center gap-1 rounded border border-dashed border-ws-border px-2 py-1 text-ws-muted hover:bg-ws-hover hover:text-ws-text'>
                      <Plus className='h-3.5 w-3.5' />{t('board.addAffiliation')}
                    </button>
                  )}
                  <button onClick={() => setEditingCollection(true)} aria-label={t('board.editCollection')} title={t('board.editCollection')}
                    className='flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-ws-muted hover:bg-ws-hover hover:text-ws-text'>
                    <Settings className='h-4 w-4' />
                  </button>
                </div>
              )}
            </div>
            <div className='flex shrink-0 items-center gap-2'>
              {view === 'board' && (
                <button onClick={() => setGroupByCategory((g) => !g)} aria-pressed={groupByCategory} title={t('board.groupByCategory')}
                  className={`inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border px-2 text-sm font-medium sm:px-3 ${groupByCategory ? 'border-ws-primary bg-ws-surface text-ws-text-link' : 'border-ws-border bg-ws-btn text-ws-text hover:bg-ws-hover'}`}>
                  <Layers className='h-4 w-4' /><span className='hidden sm:inline'>{t('board.groupByCategory')}</span>
                </button>
              )}
              <div className='inline-flex shrink-0 rounded-lg bg-ws-lane p-0.5'>
                <button onClick={() => setView('board')} aria-label={t('board.view.board')} className={`flex h-8 w-9 items-center justify-center rounded-md ${view === 'board' ? 'bg-ws-surface text-ws-text shadow-sm' : 'text-ws-muted'}`}><LayoutGrid className='h-4 w-4' /></button>
                <button onClick={() => setView('list')} aria-label={t('board.view.list')} className={`flex h-8 w-9 items-center justify-center rounded-md ${view === 'list' ? 'bg-ws-surface text-ws-text shadow-sm' : 'text-ws-muted'}`}><List className='h-4 w-4' /></button>
              </div>
              <Button variant='primary' onClick={handleNewExperience} title={t('board.newExperience')} className='shrink-0'>
                <Plus className='h-4 w-4' /><span className='hidden sm:inline'>{t('board.newExperience')}</span>
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <p className='flex-1 pt-16 text-center text-sm text-ws-muted'>{t('common.loading')}</p>
        ) : error ? (
          <p className='flex-1 pt-16 text-center text-sm text-red-500'>{error}</p>
        ) : view === 'board' ? (
          <div className='flex flex-1 flex-col overflow-hidden'>
            {/* Phone-only pipeline switcher: pick one column to view (no sideways scrolling).
                Base `hidden` + `max-sm:flex` so it reliably disappears at sm+ (a base `flex` would
                win over `sm:hidden` and leak into the multi-column view). */}
            <div className='hidden gap-1.5 px-4 pb-2 max-sm:flex'>
              {COLUMNS.map((col) => (
                <button key={col.id} onClick={() => setMobileCol(col.id)} aria-pressed={mobileCol === col.id}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs font-medium ${mobileCol === col.id ? 'border-ws-primary bg-ws-surface text-ws-text' : 'border-ws-border bg-ws-btn text-ws-muted hover:bg-ws-hover'}`}>
                  <span className='truncate'>{t(col.labelKey)}</span>
                  <span className='shrink-0 opacity-60'>{orderedByCol[col.id].length}</span>
                </button>
              ))}
            </div>
            <div className='flex flex-1 gap-4 overflow-x-auto px-4 pb-3 sm:px-6'>
              {COLUMNS.map((col) => {
                const cards = orderedByCol[col.id];
                const pinned = cards.filter((e) => isPinned(e));
                const rest = cards.filter((e) => !isPinned(e));
                const catSections = groupByCategory ? buildCategorySections(rest, t('board.ungrouped')) : null;
                const renderCard = (exp: ExperienceSummary, keyPrefix = '') => (
                  <ExperienceCard
                    key={`${keyPrefix}${exp.id}`}
                    experience={{ ...exp, pinned: isPinned(exp) }}
                    kanbanStatus={col.id}
                    onEdit={() => handleEdit(exp)}
                    onSeeResults={() => showToast(t('home.seeResultsNotImplemented'))}
                    onPreview={() => showToast(t('home.previewNotImplemented'))}
                    onTogglePin={() => togglePin(exp)}
                    onDuplicate={() => handleDuplicate(exp.id)}
                    onRemove={() => setDeleteModalId(exp.id)}
                    categoryOptions={allCategories}
                    onCategoryChange={(cat) => handleCategorySave(exp.id, cat)}
                    onCopyLink={exp.shareToken ? () => { navigator.clipboard.writeText(`http://localhost:5148/experience/${exp.shareToken}`); showToast(t('home.linkCopied')); } : undefined}
                    onSummarySave={(summary) => handleSummarySave(exp.id, summary)}
                    onProgress={col.id === 'in-progress' ? () => moveToColumn(exp.id, 'finished') : col.id === 'finished' ? () => setPublishModalId(exp.id) : undefined}
                    progressLabel={col.id === 'in-progress' ? t('board.advance.toComplete') : col.id === 'finished' ? t('board.advance.publish') : undefined}
                    progressVariant={col.id === 'finished' ? 'publish' : 'move'}
                    onRegress={col.id === 'finished' ? () => moveToColumn(exp.id, 'in-progress') : col.id === 'live' ? () => moveToColumn(exp.id, 'finished') : undefined}
                    regressLabel={col.id === 'finished' ? t('board.regress.toDevelopment') : col.id === 'live' ? t('board.regress.unpublish') : undefined}
                  />
                );
                return (
                  <BoardColumn key={col.id} headerBg={col.headerBg} label={t(col.labelKey)} count={cards.length} mobileActive={col.id === mobileCol}
                    controls={<SelectMenu value={colSort[col.id]} options={sortOptions} onChange={(v) => setColSort((s) => ({ ...s, [col.id]: v as SortMode }))} ariaLabel={t('board.sort.label')} />}
                  >
                    {pinned.length > 0 && (
                      <>
                        <div className='flex items-center gap-1.5 px-0.5 text-xs font-medium text-ws-muted'>
                          <Pin className='h-3 w-3 rotate-45 text-ws-secondary' strokeWidth={2} />{t('board.pinned')}
                        </div>
                        {pinned.map((exp) => renderCard(exp))}
                        {rest.length > 0 && <div className='my-0.5 border-t border-ws-border' />}
                      </>
                    )}
                    {catSections
                      ? catSections.map(([label, list], gi) => (
                          <Fragment key={label}>
                            {gi > 0 && <div className='my-0.5 border-t border-ws-border' />}
                            <div className='flex items-center gap-1.5 px-0.5 text-xs font-medium text-ws-muted'>{label}</div>
                            {list.map((exp) => renderCard(exp, `${label}::`))}
                          </Fragment>
                        ))
                      : rest.map((exp) => renderCard(exp))}
                    {cards.length === 0 && <div className='flex flex-1 items-center justify-center py-8'><p className='text-xs text-ws-muted'>{t('home.noExperiences')}</p></div>}
                  </BoardColumn>
                );
              })}
            </div>
            {/* Archived shelf */}
            {archived.length > 0 && (
              <div className='mx-4 mb-4 shrink-0 rounded-xl border border-ws-border bg-ws-surface sm:mx-6'>
                <button onClick={() => setShowArchived((s) => !s)} className='flex w-full items-center gap-2 px-4 py-3 text-sm font-semibold text-ws-neutral-fg'>
                  <ChevronDown className={`h-4 w-4 transition-transform ${showArchived ? '' : '-rotate-90'}`} />
                  {t('board.archivedShelf', { count: archived.length })}
                </button>
                {showArchived && (
                  <div className='flex flex-wrap gap-2 px-4 pb-4'>
                    {archived.map((e) => (
                      <span key={e.id} className='flex items-center gap-2 rounded-lg border border-ws-border bg-ws-bg px-3 py-2 text-sm text-ws-muted'>
                        {isPinned(e) && <Pin className='h-3.5 w-3.5 rotate-45 text-ws-secondary' />}{e.title}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* List view */
          <div className='flex-1 overflow-auto px-4 pb-16 sm:px-6'>
            {/* Phone: stacked rows (a table can't fit six columns on a phone without crushing Name).
                Base `hidden` + `max-sm:flex` so it reliably hides at sm+ where the table takes over. */}
            <div className='hidden flex-col gap-2 max-sm:flex'>
              {filtered.map((e) => {
                const c = colOf(getStatus(e));
                const label = c === 'archived' ? t('board.columns.archived') : t(COLUMNS.find((x) => x.id === c)!.labelKey);
                return (
                  <button key={e.id} onClick={() => handleEdit(e)} className='flex w-full flex-col gap-1 rounded-xl border border-ws-border bg-ws-surface p-3 text-left hover:bg-ws-hover'>
                    <span className='truncate font-medium text-ws-text'>{e.title}</span>
                    <span className='flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs text-ws-muted'>
                      <span className='font-medium text-ws-text-link'>{label}</span>
                      {e.category && <span>{e.category}</span>}
                      <span>{e.taskCount} {t('experience.card.task', { count: e.taskCount })}</span>
                      <span>{e.totalPoints ?? 0} {t('experience.card.points')}</span>
                      <span className='ml-auto whitespace-nowrap'>{relativeTime(e.updatedAt, i18n.language)}</span>
                    </span>
                  </button>
                );
              })}
              {filtered.length === 0 && <p className='py-10 text-center text-ws-muted'>{t('home.noExperiences')}</p>}
            </div>
            {/* Tablet/desktop: sortable table. Category shows at lg+; Updated drops the full timestamp
                below lg (relative only) so the six columns fit tablet-portrait width without scrolling. */}
            <div className='hidden overflow-hidden rounded-xl border border-ws-border bg-ws-surface sm:block'>
              <table className='w-full table-fixed text-sm'>
                <colgroup>
                  <col />
                  <col className='w-0 lg:w-40' />
                  <col className='w-16 lg:w-20' />
                  <col className='w-16 lg:w-20' />
                  <col className='w-36 lg:w-44' />
                  <col className='w-28 lg:w-72' />
                </colgroup>
                <thead>
                  <tr className='bg-ws-lane text-left text-xs font-bold uppercase tracking-wide text-ws-muted'>
                    {([['name', t('board.list.name'), false, ''], ['category', t('board.list.category'), false, 'hidden lg:table-cell'], ['tasks', t('board.list.tasks'), true, ''], ['points', t('board.list.points'), true, ''], ['status', t('board.list.status'), false, ''], ['updated', t('board.list.updated'), false, '']] as [ListKey, string, boolean, string][]).map(([key, label, right, hide]) => (
                      <th key={key} className={`px-4 py-3 ${right ? 'text-right' : ''} ${hide}`}>
                        <button type='button' onClick={() => toggleSort(key)} className='inline-flex items-center gap-1 hover:text-ws-text'>
                          {label}
                          {/* Reserve the chevron's width even when inactive so sorting doesn't shift columns. */}
                          {listSort.key === key
                            ? (listSort.dir === 'asc' ? <ChevronUp className='h-3 w-3 shrink-0' /> : <ChevronDown className='h-3 w-3 shrink-0' />)
                            : <ChevronDown className='h-3 w-3 shrink-0 opacity-0' />}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e) => {
                    const c = colOf(getStatus(e));
                    const label = c === 'archived' ? t('board.columns.archived') : t(COLUMNS.find((x) => x.id === c)!.labelKey);
                    return (
                      <tr key={e.id} onClick={() => handleEdit(e)} className='cursor-pointer border-t border-ws-border hover:bg-ws-hover'>
                        <td className='truncate px-4 py-2.5 font-medium text-ws-text'>{e.title}</td>
                        <td className='hidden truncate px-4 py-2.5 text-ws-muted lg:table-cell'>{e.category || '—'}</td>
                        <td className='px-4 py-2.5 text-right tabular-nums text-ws-muted'>{e.taskCount}</td>
                        <td className='px-4 py-2.5 text-right tabular-nums text-ws-muted'>{e.totalPoints ?? 0}</td>
                        <td className='truncate px-4 py-2.5 text-ws-muted'>{label}</td>
                        <td className='truncate px-4 py-2.5 text-ws-muted'>
                          <span className='hidden lg:inline'>{new Date(e.updatedAt).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })} · </span>
                          <span>{relativeTime(e.updatedAt, i18n.language)}</span>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && <tr><td colSpan={6} className='px-4 py-10 text-center text-ws-muted'>{t('home.noExperiences')}</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {editingCollection && collection && <CollectionModal mode='edit' initial={collection} affiliations={activeAffiliations} onClose={() => setEditingCollection(false)} onSave={handleCollectionSave} />}
      {editWarningId && <PublishedWarningModal onClose={() => setEditWarningId(null)} onProceed={() => { navigate(`/app/experience/${editWarningId}`); setEditWarningId(null); }} />}
      {publishModalId && <PublishModal onClose={() => setPublishModalId(null)} onConfirm={handleConfirmPublish} />}
      {publishedSuccess && <PublishedSuccessModal experienceName={publishedSuccess.name} shareLink={`http://localhost:5148/experience/${publishedSuccess.shareToken}`} onClose={() => setPublishedSuccess(null)} />}
      {deleteModalId && <DeleteExperienceModal experienceTitle={experiences.find((e) => e.id === deleteModalId)?.title ?? ''} onClose={() => setDeleteModalId(null)} onConfirm={handleConfirmDelete} />}
      {toast && <Toast message={toast.msg} type={toast.type} onDismiss={() => setToast(null)} />}
    </div>
  );
}

// A pipeline column: header (label + count) over a scrollable lane of cards. No drop target —
// cards change stage via their advance affordance / bulk actions, not by dragging.
// `mobileActive` controls the phone-only single-column view: below `sm` only the active column
// shows (chosen via the segmented switcher); at `sm`+ all three columns show side by side.
function BoardColumn({ headerBg, label, count, controls, children, mobileActive = true }: {
  headerBg: string; label: string; count: number; controls?: React.ReactNode; children: React.ReactNode; mobileActive?: boolean;
}) {
  return (
    <div className={`${mobileActive ? 'flex' : 'hidden sm:flex'} w-72 min-w-[220px] flex-1 flex-col overflow-hidden rounded-xl lg:min-w-[260px]`}>
      <div className={`flex flex-wrap items-center gap-2 rounded-t-xl px-4 py-3 ${headerBg}`}>
        <span className='text-md font-semibold text-ws-text'>{label}</span>
        <span className='text-xs font-medium text-ws-text opacity-60'>{count}</span>
        {controls && <div className='ml-auto flex items-center gap-2'>{controls}</div>}
      </div>
      <div className='flex flex-1 flex-col gap-2 overflow-y-auto rounded-b-xl bg-ws-lane p-3'>
        {children}
      </div>
    </div>
  );
}
